import { useBoolean, useOutsideClick } from '@chakra-ui/hooks'
import { IconProps } from '@chakra-ui/icon'
import { Tooltip } from '@chakra-ui/tooltip'
import React, { useMemo } from 'react'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { MangoButton } from '~/components/shared/MangoButton'
import { hasValue } from '~/utils/common'

import { Menu, MenuOptions, MenuPositioningWrapper } from './Menu'
import { MenuItem } from './MenuItem'

export type MangoButtonMenuItem<TValue> = MenuItem<TValue> & {
  buttonLabel?: string
}

type DropdownMangoButtonProps<TValue> = {
  variant: MangoButtonVariant
  tooltipLabel?: string | React.ReactNode
  leftIcon?: SVGComponent
  rightIcon?: SVGComponent
  onButtonClick?: () => void
  items: MangoButtonMenuItem<TValue>[]
  selectedIndex?: number
  onSelect?: (value: TValue, index: number) => string | void
  onMenuOpenChange?: (isOpen: boolean) => void
  menuOptions?: MenuOptions
  mangoButtonOptions?: {
    leftIconProps?: IconProps
    rightIconProps?: IconProps
  }
} & (
  | { label: string; placeholder?: never }
  | { label?: never; placeholder: string }
)

export const DropdownMangoButton = <TValue,>({
  label,
  placeholder,
  variant,
  tooltipLabel,
  leftIcon,
  rightIcon,
  onButtonClick,
  items,
  selectedIndex,
  onSelect,
  onMenuOpenChange,
  menuOptions,
  mangoButtonOptions,
}: DropdownMangoButtonProps<TValue>) => {
  const [internalIndex, setInternalIndex] = React.useState<number>(0)
  const [menuOpen, setMenuOpen] = useBoolean(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      if (menuOpen) {
        setMenuOpen.off()
        onMenuOpenChange?.(false)
      }
    },
  })

  const buttonLabel = useMemo(() => {
    const index = selectedIndex ?? internalIndex
    const selectedItem = items[index]

    return selectedItem?.buttonLabel ?? label ?? placeholder
  }, [items, selectedIndex, internalIndex])

  const handleButtonClick = () => {
    setMenuOpen.toggle()
    onMenuOpenChange?.(!menuOpen)
    onButtonClick?.()
  }
  const handleSelect = (value: TValue, index: number) => {
    onSelect?.(value, index)
    setInternalIndex(index)
    setMenuOpen.off()
    onMenuOpenChange?.(false)
  }

  return (
    <MenuPositioningWrapper ref={containerRef}>
      <Tooltip
        hasArrow
        label={tooltipLabel}
        whiteSpace="pre-wrap"
        placement="top"
        isDisabled={!hasValue(tooltipLabel)}
      >
        <MangoButton
          ref={buttonRef}
          variant={variant}
          leftIcon={leftIcon}
          leftIconProps={mangoButtonOptions?.leftIconProps}
          rightIcon={rightIcon}
          rightIconProps={mangoButtonOptions?.rightIconProps}
          onClick={handleButtonClick}
        >
          {buttonLabel}
        </MangoButton>
      </Tooltip>
      {menuOpen && items.length > 0 && (
        <Menu
          anchorRef={buttonRef}
          items={items}
          onSelect={handleSelect}
          options={menuOptions}
        />
      )}
    </MenuPositioningWrapper>
  )
}
